import { useRef, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsClientAssetModel } from 'src/api/generated';
import { useGlobal } from 'src/contexts/GlobalContext';
import FileUpload from 'src/pages/Devices/GroupDetails/TabPages/GroupDesign/components/FileUpload';
import { FileData } from 'src/pages/Devices/GroupDetails/TabPages/GroupDesign/components/FileUpload/FileUpload';
import { sanitizeFileName } from 'src/utils/sanitize';

import { AddFileDrawer } from './AddFileDrawer';
import { SelectAssetLink } from './AssetPicker.styled';
import { AssetPickerDialog, AssetSelectionMode } from './AssetPickerDialog';
import { useAssetLibraryUpload } from './useAssetLibraryUpload';

export interface AssetUploadFieldProps {
  value?: FileData;
  // The asset is only passed when one was picked from the library, so callers that store a reference
  // can tell a library pick apart from a file dropped straight on the field.
  onChange: (fileData: FileData | string | null, asset?: FanguardApiContractsClientAssetModel) => void;
  assets?: FanguardApiContractsClientAssetModel[];
  isAssetsLoading?: boolean;
  accept?: string;
  maxSizeInMB?: number;
  minWidth?: number;
  minHeight?: number;
  description?: string;
  dialogTitle?: string;
  selectedAssetId?: string | null;
  selectionMode?: AssetSelectionMode;
  uploadToLibrary?: boolean;
  onValidateFile?: (fileData: FileData) => boolean;
}

export const AssetUploadField = ({
  value,
  onChange,
  assets = [],
  isAssetsLoading,
  accept,
  maxSizeInMB,
  minWidth,
  minHeight,
  description,
  dialogTitle,
  selectedAssetId,
  selectionMode,
  uploadToLibrary = true,
  onValidateFile,
}: AssetUploadFieldProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isDesktop } = useGlobal();
  const { uploadFileToLibrary } = useAssetLibraryUpload();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isAddFileOpen, setIsAddFileOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const openPicker = () => {
    setIsAddFileOpen(false);
    setIsPickerOpen(true);
  };

  // Clicking the dragger's own input keeps FileUpload's accept, size and dimension validation
  // instead of duplicating it behind a second file input.
  const openDeviceFilePicker = () => {
    setIsAddFileOpen(false);
    wrapperRef.current?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  };

  const handleSelectAssetClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openPicker();
  };

  // Files dropped or picked straight on the field bypass the dialog, so they are added to the
  // library here. A reference field only stores an assetId, so it hears about the file once the
  // library has it — never as raw bytes.
  const handleFieldChange = async (fileData: FileData | string | null) => {
    const isReference = selectionMode === 'reference';

    if (!isReference || !fileData || typeof fileData === 'string') onChange(fileData);

    if (!fileData || typeof fileData === 'string' || !uploadToLibrary) return;

    const name = sanitizeFileName(fileData.name || '');
    const existingAsset = assets.find((asset) => sanitizeFileName(asset.name || '') === name);

    if (existingAsset) {
      if (isReference) onChange(fileData, existingAsset);

      return;
    }

    const dataUrl = fileData.image || fileData.url || '';

    if (!dataUrl.startsWith('data:')) return;

    try {
      const created = await uploadFileToLibrary(fileData, { resolveAsset: isReference });

      if (isReference) {
        if (created?.id) onChange(fileData, created);
        else message.error(t('assets.uploadError'));
      }
    } catch {
      // The form already holds the file, so a failed library upload only shows its own message.
    }
  };

  const label = isDesktop ? (
    <>
      {`${t('assets.dropFileHereOr')} `}
      <SelectAssetLink onClick={handleSelectAssetClick} data-testid="select-asset-link">
        {t('assets.selectAsset')}
      </SelectAssetLink>
    </>
  ) : (
    `${t('assets.dropFileHereOr')} ${t('assets.selectAsset')}`
  );

  return (
    <div ref={wrapperRef}>
      <FileUpload
        value={value}
        onChange={handleFieldChange}
        accept={accept}
        maxSizeInMB={maxSizeInMB}
        minWidth={minWidth}
        minHeight={minHeight}
        description={description}
        label={label}
        openFileDialogOnClick={false}
        onClick={isDesktop ? openPicker : () => setIsAddFileOpen(true)}
        onValidateFile={onValidateFile}
      />
      {!isDesktop && (
        <AddFileDrawer
          open={isAddFileOpen}
          onClose={() => setIsAddFileOpen(false)}
          onChooseFromLibrary={openDeviceFilePicker}
          onSelectFromAssets={openPicker}
        />
      )}
      <AssetPickerDialog
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={onChange}
        assets={assets}
        isLoading={isAssetsLoading}
        title={dialogTitle}
        accept={accept}
        maxSizeInMB={maxSizeInMB}
        minWidth={minWidth}
        minHeight={minHeight}
        selectedAssetId={selectedAssetId}
        selectionMode={selectionMode}
        uploadToLibrary={uploadToLibrary}
        onValidateFile={onValidateFile}
      />
    </div>
  );
};
